import React, { useState, useCallback, useRef } from 'react';
import { ElementType } from '@goldcast/player-config/src/types/Element/Type';
import { VideoElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import BaseTooltip from './BaseTooltip';
import appLogger from '@/services/logger';
import AudioOverlayControls from '@/Pages/Clip/ClipPlayer/ClipAssets/AudioOverlay/AudioOverlayControls';
import VideoOverlayTooltip from '@/Pages/Clip/ClipPlayer/ClipAssets/VideoOverlay/VideoOverlayTooltip';
import IconButton from '@/components/atoms/Button/IconButton';

interface ElementTooltipProps {
  element: {
    id: string;
    name: string;
    type: ElementType;
    [key: string]: any;
  };
  triggerRef: React.RefObject<HTMLDivElement>;
  onDelete?: (elementId: string) => void;
  onUpdate?: (elementId: string, updates: any) => void;
}

const ElementTooltip: React.FC<ElementTooltipProps> = ({ element, triggerRef, onDelete, onUpdate }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateRef = useRef<string>('');

  // Function to update element
  const updateElement = useCallback(
    async (updates: any) => {
      if (onUpdate) {
        // Create a unique key for this update to prevent duplicate calls
        const updateKey = JSON.stringify(updates);

        if (lastUpdateRef.current === updateKey) {
          return; // Skip if this exact update was already sent
        }

        setIsUpdating(true);
        try {
          await onUpdate(element.id, updates);
          lastUpdateRef.current = updateKey;
        } catch (error) {
          appLogger.error('Failed to update element', error);
        } finally {
          setIsUpdating(false);
        }
      }
    },
    [element.id, onUpdate]
  );

  // Debounced update function
  const debouncedUpdate = useCallback(
    (updates: any) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        updateElement(updates);
      }, 300);
    },
    [updateElement]
  );

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleDelete = useCallback(
    (closeTooltip: () => void) => {
      if (onDelete) {
        onDelete(element.id);
      }
      closeTooltip();
    },
    [element.id, onDelete]
  );

  const getElementTypeName = useCallback(() => {
    switch (element.type) {
      case ElementType.VIDEO:
        return element.name ? element.name : 'Video';
      case ElementType.AUDIO:
        return 'Audio';
      case ElementType.IMAGE:
        return 'Image';
      case ElementType.LOTTIE:
        return 'Lottie';
      case ElementType.TEXT:
        return element.details?.text || 'Text';
      default:
        return 'Element';
    }
  }, [element.type, element.name, element.details?.text]);

  const isAudioElement = element.type === ElementType.AUDIO;
  const isVideoElement = element.type === ElementType.VIDEO;

  if (isVideoElement) {
    return <VideoOverlayTooltip element={element as VideoElement} triggerRef={triggerRef} onDelete={onDelete} />;
  }

  return (
    <BaseTooltip triggerRef={triggerRef}>
      {closeTooltip => (
        <div className="min-w-0 max-w-xs cursor-default space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="truncate text-sm font-semibold text-gray-800">{getElementTypeName()}</h3>
            {onDelete && element.type !== ElementType.TEXT && (
              <IconButton
                icon="IconTrash"
                title="Delete Element"
                onMouseDown={() => handleDelete(closeTooltip)}
                trackingId="delete-element-button"
                variation="text"
                size="small"
              />
            )}
          </div>

          {/* Audio Controls */}
          {isAudioElement && <AudioOverlayControls element={element} debouncedUpdate={debouncedUpdate} />}

          {isUpdating && (
            <div className="text-center">
              <span className="text-xs text-gray-500">Updating...</span>
            </div>
          )}
        </div>
      )}
    </BaseTooltip>
  );
};

export default ElementTooltip;
